import { useState, useEffect, useRef, useMemo } from 'react'
import Accordion from '../Accordion'

// Line icons per FAQ group. Stroked with currentColor so each icon takes the
// colour of its label (muted by default, brand green when active).
// Add a new entry here when a new group appears in the data.
const ICON_PATHS = {
  Visa: <><rect x="4" y="2.5" width="16" height="19" rx="2" /><circle cx="12" cy="9.5" r="2.8" /><path d="M8.5 17h7" /></>,
  Guide: <><circle cx="12" cy="12" r="9" /><path d="M15.8 8.2 13.9 14 8.2 15.8 10.1 10z" /></>,
  Permits: <><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M9 6v12" /><path d="M13 10.5h5M13 14h3" /></>,
  Cost: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /><path d="M14.5 9.5a2.5 2.5 0 0 0-2.5-1.5c-1.4 0-2.5.8-2.5 2s1.1 2 2.5 2 2.5.8 2.5 2-1.1 2-2.5 2a2.5 2.5 0 0 1-2.5-1.5" /></>,
  Booking: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10.5h18" /></>,
  Accommodation: <><path d="M3 10.5 12 3.5l9 7" /><path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" /><path d="M9.5 21v-5.5h5V21" /></>,
  Safety: <><path d="M12 3.2 5 6v5.6c0 4.4 2.9 8 7 9.2 4.1-1.2 7-4.8 7-9.2V6z" /><path d="M9.3 12.2 11.3 14.2 14.9 10.4" /></>,
}

function GroupIcon({ name }) {
  const path = ICON_PATHS[name]
  if (!path) return null
  return (
    <svg
      width="19" height="19" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0, opacity: 0.9 }}
    >
      {path}
    </svg>
  )
}

const slug = g => 'faq-' + g.toLowerCase().replace(/[^a-z0-9]+/g, '-')

// Grouped FAQ: category sidebar on the left, grouped accordions on the right.
// Reads trek.faq — [{ group, question, answer }]. Entries still marked TODO are
// hidden so placeholder text never reaches a visitor.
export default function TrekFAQGroups({ trek }) {
  const [expanded, setExpanded] = useState({})   // { [group]: bool } — Expand All per group
  const [active, setActive] = useState(null)
  const tabRefs = useRef({})
  const sidebarRef = useRef(null)

  // Group the flat list, preserving the order groups first appear in the data.
  const groups = useMemo(() => {
    const items = (trek?.faq || []).filter(
      f => f.question && !f.question.startsWith('TODO') && f.answer && !f.answer.startsWith('TODO')
    )
    const map = new Map()
    items.forEach(f => {
      if (!map.has(f.group)) map.set(f.group, [])
      map.get(f.group).push(f)
    })
    return [...map.entries()].map(([name, items]) => ({ name, items }))
  }, [trek])

  // Highlight the sidebar entry for the group currently in view.
  useEffect(() => {
    if (!groups.length) return
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id) }),
      { rootMargin: '-140px 0px -65% 0px', threshold: 0 }
    )
    groups.forEach(g => {
      const el = document.getElementById(slug(g.name))
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [groups])

  // Keep the active category visible in the sidebar.
  // IMPORTANT: this must never scroll the page. scrollIntoView() would walk up to
  // the nearest scrollable ancestor — the document on desktop, where the sidebar
  // isn't a scroll container — and yank the reader back up as the sticky sidebar
  // left the viewport. So adjust this container's own scrollLeft instead, and
  // only when it actually overflows (i.e. the mobile pill row).
  useEffect(() => {
    const el = tabRefs.current[active]
    const box = sidebarRef.current
    if (!el || !box) return
    if (box.scrollWidth <= box.clientWidth) return   // desktop: nothing to scroll

    const left = el.offsetLeft
    const right = left + el.offsetWidth
    if (left < box.scrollLeft) {
      box.scrollLeft = left
    } else if (right > box.scrollLeft + box.clientWidth) {
      box.scrollLeft = right - box.clientWidth
    }
  }, [active])

  if (!groups.length) return null

  const go = name => {
    setActive(slug(name))
    document.getElementById(slug(name))?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '40px' }}>
        Frequently asked questions.
      </h2>

      <div className="faq-layout">
        {/* Category sidebar */}
        <aside className="faq-sidebar">
          <div className="faq-sidebar-inner" ref={sidebarRef}>
            {groups.map(g => {
              const id = slug(g.name)
              const isActive = active === id
              return (
                <button
                  key={g.name}
                  ref={el => { tabRefs.current[id] = el }}
                  onClick={() => go(g.name)}
                  className={`faq-cat${isActive ? ' is-active' : ''}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <GroupIcon name={g.name} />
                  {g.name}
                </button>
              )
            })}
          </div>
        </aside>

        {/* Grouped accordions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {groups.map(g => {
            const id = slug(g.name)
            const isOpen = !!expanded[g.name]
            return (
              <div key={g.name} id={id} className="anchor-target" style={{
                border: '1px solid var(--stone-mid)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                background: 'var(--white)',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: '16px', padding: '16px 24px',
                  borderBottom: '1px solid var(--stone-mid)',
                }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 600, fontFamily: 'var(--serif)' }}>
                    {g.name}
                  </h3>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [g.name]: !isOpen }))}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--green)', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                  >
                    {isOpen ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>

                <div style={{ padding: '0 24px' }}>
                  <Accordion
                    items={g.items}
                    exclusive={false}
                    openAll={isOpen}
                    defaultOpenIndex={null}
                    renderQuestion={item => item.question}
                    renderAnswer={item => item.answer}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <style>{`
        .faq-layout {
          display: grid;
          grid-template-columns: 280px minmax(0, 1fr);
          gap: 40px;
          align-items: start;
        }
        /* Tinted panel so the sidebar reads as its own surface, distinct from
           the white accordion cards and the section background behind it. */
        .faq-sidebar-inner {
          position: sticky;
          top: 130px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          background: var(--stone-mid);
          border-radius: var(--radius-lg);
          padding: 12px;
        }
        .faq-cat {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 13px 14px;
          border: none;
          border-radius: var(--radius);
          background: transparent;
          color: var(--ink-mid);
          font-family: var(--sans);
          font-size: 15px;
          font-weight: 400;
          text-align: left;
          cursor: pointer;
          white-space: nowrap;
          transition: var(--transition);
        }
        .faq-cat:hover { color: var(--green); }
        .faq-cat.is-active {
          background: var(--white);
          color: var(--green);
          font-weight: 600;
          box-shadow: var(--shadow-sm);
        }

        @media (max-width: 860px) {
          .faq-layout { grid-template-columns: minmax(0, 1fr); gap: 20px; }
          /* Sidebar becomes a horizontal swipe row above the questions */
          .faq-sidebar-inner {
            position: static;
            flex-direction: row;
            overflow-x: auto;
            gap: 8px;
            padding: 8px;
            scrollbar-width: none;
          }
          .faq-sidebar-inner::-webkit-scrollbar { display: none; }
          .faq-cat {
            width: auto;
            flex-shrink: 0;
            background: var(--white);
            border-radius: 40px;
            padding: 10px 16px;
            font-size: 14px;
          }
        }
      `}</style>
    </>
  )
}
